import type { Stroke, Point } from '@roll1/shared';

interface SpatialIndexCell {
  strokeIds: Set<string>;
}

interface StrokeCellBounds {
  minCol: number;
  maxCol: number;
  minRow: number;
  maxRow: number;
}

/**
 * Grid-based spatial index for fast hit-testing and area queries on strokes.
 * Each cell covers a cellSize x cellSize area in world coordinates.
 */
export class GridSpatialIndex {
  private cellSize: number;
  private cells: Map<string, SpatialIndexCell> = new Map();
  private strokeBounds: Map<string, StrokeCellBounds> = new Map();

  constructor(cellSize: number) {
    this.cellSize = cellSize;
  }

  private cellKey(col: number, row: number): string {
    return `${col},${row}`;
  }

  private getStrokeBBox(stroke: Stroke): { minX: number; minY: number; maxX: number; maxY: number } {
    if (stroke.type === 'path' && stroke.points && stroke.points.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of stroke.points) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
      return { minX, minY, maxX, maxY };
    }
    const x1 = stroke.x1 ?? 0;
    const y1 = stroke.y1 ?? 0;
    const x2 = stroke.x2 ?? 0;
    const y2 = stroke.y2 ?? 0;
    return {
      minX: Math.min(x1, x2),
      minY: Math.min(y1, y2),
      maxX: Math.max(x1, x2),
      maxY: Math.max(y1, y2),
    };
  }

  /** Add a stroke to the index */
  insert(stroke: Stroke): void {
    const bbox = this.getStrokeBBox(stroke);
    const pad = stroke.lineWidth / 2;
    const minCol = Math.floor((bbox.minX - pad) / this.cellSize);
    const maxCol = Math.floor((bbox.maxX + pad) / this.cellSize);
    const minRow = Math.floor((bbox.minY - pad) / this.cellSize);
    const maxRow = Math.floor((bbox.maxY + pad) / this.cellSize);

    this.strokeBounds.set(stroke.id, { minCol, maxCol, minRow, maxRow });

    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        const key = this.cellKey(col, row);
        let cell = this.cells.get(key);
        if (!cell) {
          cell = { strokeIds: new Set() };
          this.cells.set(key, cell);
        }
        cell.strokeIds.add(stroke.id);
      }
    }
  }

  /** Remove a stroke from the index by ID */
  remove(strokeId: string): void {
    const bounds = this.strokeBounds.get(strokeId);
    if (!bounds) return;

    for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
      for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
        const key = this.cellKey(col, row);
        const cell = this.cells.get(key);
        if (cell) {
          cell.strokeIds.delete(strokeId);
          if (cell.strokeIds.size === 0) this.cells.delete(key);
        }
      }
    }
    this.strokeBounds.delete(strokeId);
  }

  /** Update a stroke position (remove + insert) */
  update(stroke: Stroke): void {
    this.remove(stroke.id);
    this.insert(stroke);
  }

  /** Find all stroke IDs in a given rectangular area (world coords) */
  query(bounds: { x: number; y: number; width: number; height: number }): string[] {
    const minCol = Math.floor(bounds.x / this.cellSize);
    const maxCol = Math.floor((bounds.x + bounds.width) / this.cellSize);
    const minRow = Math.floor(bounds.y / this.cellSize);
    const maxRow = Math.floor((bounds.y + bounds.height) / this.cellSize);

    const resultSet = new Set<string>();
    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        const cell = this.cells.get(this.cellKey(col, row));
        if (cell) {
          for (const id of cell.strokeIds) resultSet.add(id);
        }
      }
    }
    return [...resultSet];
  }

  /** Hit-test: find closest stroke to point within radius */
  hitTest(strokes: Stroke[], px: number, py: number, radius: number): string | null {
    // Collect candidate IDs from cells within radius
    const minCol = Math.floor((px - radius) / this.cellSize);
    const maxCol = Math.floor((px + radius) / this.cellSize);
    const minRow = Math.floor((py - radius) / this.cellSize);
    const maxRow = Math.floor((py + radius) / this.cellSize);

    const candidateIds = new Set<string>();
    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        const cell = this.cells.get(this.cellKey(col, row));
        if (cell) {
          for (const id of cell.strokeIds) candidateIds.add(id);
        }
      }
    }

    if (candidateIds.size === 0) return null;

    // Build a lookup map for candidates
    let bestId: string | null = null;
    let bestDist = radius;

    for (const stroke of strokes) {
      if (!candidateIds.has(stroke.id)) continue;
      const d = this.distanceToStroke(stroke, px, py);
      if (d < bestDist) {
        bestDist = d;
        bestId = stroke.id;
      }
    }

    return bestId;
  }

  /** Full rebuild of the index from an array of strokes */
  rebuild(strokes: Stroke[]): void {
    this.clear();
    for (const stroke of strokes) {
      this.insert(stroke);
    }
  }

  /** Clear the index */
  clear(): void {
    this.cells.clear();
    this.strokeBounds.clear();
  }

  /** Distance from a point to a stroke */
  private distanceToStroke(stroke: Stroke, px: number, py: number): number {
    if (stroke.type === 'path' && stroke.points && stroke.points.length > 0) {
      let minDist = Infinity;
      if (stroke.points.length === 1) {
        return dist(stroke.points[0].x, stroke.points[0].y, px, py);
      }
      for (let i = 0; i < stroke.points.length - 1; i++) {
        const d = pointToSegmentDistance(px, py, stroke.points[i], stroke.points[i + 1]);
        if (d < minDist) minDist = d;
      }
      return minDist;
    }

    if (stroke.type === 'line') {
      return pointToSegmentDistance(px, py,
        { x: stroke.x1!, y: stroke.y1! },
        { x: stroke.x2!, y: stroke.y2! });
    }

    if (stroke.type === 'rect') {
      return distanceToRect(px, py, stroke.x1!, stroke.y1!, stroke.x2!, stroke.y2!);
    }

    if (stroke.type === 'circle') {
      return distanceToEllipse(px, py, stroke.x1!, stroke.y1!, stroke.x2!, stroke.y2!);
    }

    return Infinity;
  }
}

function dist(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

function pointToSegmentDistance(px: number, py: number, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return dist(px, py, a.x, a.y);
  let t = ((px - a.x) * dx + (py - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return dist(px, py, projX, projY);
}

function distanceToRect(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);
  // Distance to the closest edge of the rectangle
  const edges: [Point, Point][] = [
    [{ x: minX, y: minY }, { x: maxX, y: minY }],
    [{ x: maxX, y: minY }, { x: maxX, y: maxY }],
    [{ x: maxX, y: maxY }, { x: minX, y: maxY }],
    [{ x: minX, y: maxY }, { x: minX, y: minY }],
  ];
  let minDist = Infinity;
  for (const [a, b] of edges) {
    const d = pointToSegmentDistance(px, py, a, b);
    if (d < minDist) minDist = d;
  }
  return minDist;
}

function distanceToEllipse(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const cx = (x1 + x2) / 2;
  const cy = (y1 + y2) / 2;
  const rx = Math.abs(x2 - x1) / 2;
  const ry = Math.abs(y2 - y1) / 2;
  if (rx === 0 && ry === 0) return dist(px, py, cx, cy);
  // Approximate: distance to nearest point on ellipse
  const angle = Math.atan2((py - cy) * rx, (px - cx) * ry);
  const nearX = cx + rx * Math.cos(angle);
  const nearY = cy + ry * Math.sin(angle);
  return dist(px, py, nearX, nearY);
}
